import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Archive, ArrowDown, ArrowUp, CalendarDays, ChevronsUpDown, Crosshair, History,
  LayoutGrid, List, Pause, Pencil, Play, PlaySquare, Radio, Replace, ScrollText,
  Search, ShieldCheck, Square, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import { Pagination } from "@/components/ui/kit";
import { JobCard, statusColor } from "./jobs/JobCard";
import { JobFormDialog } from "./jobs/JobFormDialog";
import { MassReplaceDialog } from "./jobs/MassReplaceDialog";
import { OperationHistory } from "./jobs/WholePostComposer";
import { emptyForm, formFromJob, validateForm, buildRequest, jobActions } from "./jobs/jobForm";
import {
  CREATED_LABELS, createdBounds, createdFilterLabel, formatCreated,
  localInputToIso, matchesCreatedRange,
} from "./jobs/jobDates";

const MODE_META = {
  live: { icon: Radio, color: "var(--c-sky)", label: "live" },
  historical: { icon: History, color: "var(--c-teal)", label: "historical" },
  mass_replace: { icon: Replace, color: "var(--c-violet)", label: "mass replace" },
  archive: { icon: Archive, color: "var(--c-amber)", label: "archive" },
  replay: { icon: PlaySquare, color: "var(--c-emerald)", label: "replay" },
};

const STATUS_ORDER = { error: 0, rate_limited: 1, running: 2, queued: 3, paused: 4, stopped: 5, idle: 6, completed: 7 };
const SORT_VALUES = new Set(["created_desc", "created_asc", "activity_desc", "activity_asc", "status", "name_asc", "name_desc"]);
const CREATED_VALUES = new Set(Object.keys(CREATED_LABELS));

const sortFromParams = (params) => {
  const sort = params.get("sort");
  const order = params.get("order") === "asc" ? "asc" : "desc";
  if (sort === "created_at") return `created_${order}`;
  if (sort === "last_activity") return `activity_${order}`;
  if (sort === "name") return `name_${order}`;
  return SORT_VALUES.has(sort) ? sort : "created_desc";
};

const validPage = (value, fallback = 0) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const dateValue = (value) => {
  const parsed = value ? new Date(value).getTime() : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

const compareDate = (field, direction) => (a, b) => {
  const left = dateValue(a[field]);
  const right = dateValue(b[field]);
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return direction === "asc" ? left - right : right - left;
};

const localInputParts = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return { date: "", time: "" };
  const pad = (number) => String(number).padStart(2, "0");
  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
};

const relativeTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

function JobTableRow({ j, onStart, onStop, onPause, onResume, onLogs, onEdit, onRemove, onValidate, onRepickDestination, validating }) {
  const meta = MODE_META[j.mode] || MODE_META.historical;
  const Icon = meta.icon;
  const color = statusColor[j.status] || "var(--tg-faint)";
  const ckpt = j.mode === "live" ? j.live_last_id : j.last_id;
  const lastActivity = relativeTime(j.last_copied_at);
  const hasRetry = j.live_retry_from !== null && j.live_retry_from !== undefined;
  const created = formatCreated(j.created_at);
  const diag = j.diagnostics;
  // Persisted verdict, so the blocked marker and the corrective action survive a
  // restart that emptied the in-process peer cache.
  const destBlocked = diag?.destination_blocked
    || diag?.last_validation?.code === "DESTINATION_NOT_ACCESSIBLE";
  const acctTitle = [diag?.source_account, diag?.dest_account]
    .filter(Boolean)
    .map((a) => `${a.label || a.account_id}: ${a.ready ? "ready" : (a.runtime_status || "unknown")}`)
    .join(" · ");
  return (
    <tr className="border-b border-[var(--tg-line-soft)] hover:bg-[var(--tg-hover)] transition-colors" data-testid={`job-row-${j.id}`}>
      <td className="py-2.5 px-3 min-w-0 max-w-[260px]">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: meta.color }} />
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate">{j.name}</div>
            <div className="text-[10px] text-[var(--tg-faint)] font-mono truncate">
              {j.mode === "mass_replace"
                ? j.source
                : j.mode === "archive"
                ? `${j.source} → Vault`
                : `${j.source} → ${(j.destinations || []).join(", ")}`}
            </div>
          </div>
        </div>
      </td>
      <td className="py-2.5 px-3 hidden lg:table-cell">
        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
              style={{ color: meta.color, background: `${meta.color}1a` }}>{meta.label}</span>
      </td>
      <td className="py-2.5 px-3 hidden md:table-cell text-xs text-[var(--tg-muted)] truncate max-w-[120px]" title={acctTitle || undefined}>
        <span className="inline-flex items-center gap-1.5 min-w-0">
          {diag?.source_account && (
            <span
              className="h-1.5 w-1.5 rounded-full shrink-0"
              style={{ background: diag.source_account.ready ? "var(--c-emerald)"
                : diag.source_account.recovering ? "var(--c-amber)" : "var(--c-rose)" }}
              data-testid={`job-account-runtime-${j.id}`}
            />
          )}
          <span className="truncate">
            {j.source_account_name || "—"}
            {j.dest_account_name && j.dest_account_name !== j.source_account_name ? ` → ${j.dest_account_name}` : ""}
          </span>
        </span>
      </td>
      <td className="py-2.5 px-3">
        <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 whitespace-nowrap"
              style={{ color, background: `${color}1a` }}
              title={j.status === "error" ? (j.error || j.last_error || "failed") : undefined}>
          {j.status === "running" && <span className="live-dot h-1 w-1 rounded-full" style={{ background: color }} />}
          {j.status === "rate_limited" ? "rate limit" : j.status}
        </span>
        {hasRetry && (
          <span className="ml-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
                style={{ color: "var(--c-pink)", background: "rgb(var(--c-pink-rgb)/0.1)" }}
                title={`Incomplete items will be retried from message #${j.live_retry_from + 1}`}>
            retry
          </span>
        )}
        {j.mode === "replay" && j.replay_delay != null && (
          <span className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
                style={{ color: "var(--c-emerald)", background: "rgb(var(--c-emerald-rgb)/0.1)" }}
                title="Fixed delay between replayed messages" data-testid={`job-replay-delay-${j.id}`}>
            {j.replay_delay}s
          </span>
        )}
        {destBlocked && (
          <span className="ml-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
                style={{ color: "var(--c-rose)", background: "rgb(var(--c-rose-rgb)/0.1)" }}
                title={diag.destination_blocked_reason || diag.last_validation?.reason}
                data-testid={`job-dest-blocked-${j.id}`}>
            destination unreachable
          </span>
        )}
        {!destBlocked && diag?.waiting_reason && (
          <span className="ml-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
                style={{ color: "var(--c-amber)", background: "rgb(var(--c-amber-rgb)/0.1)" }}
                title={`Waiting: ${diag.waiting_reason}`}
                data-testid={`job-waiting-${j.id}`}>
            waiting
          </span>
        )}
        {diag?.last_validation?.state === "VALIDATION_FAILED" && !destBlocked && (
          <span className="ml-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
                style={{ color: "var(--c-rose)", background: "rgb(var(--c-rose-rgb)/0.1)" }}
                title={diag.last_validation.reason}
                data-testid={`job-invalid-${j.id}`}>
            validation failed
          </span>
        )}
      </td>
      <td className="py-2.5 px-3 hidden lg:table-cell text-[11px] text-[var(--tg-muted)] whitespace-nowrap" title={created?.tooltip} data-testid={`job-created-${j.id}`}>
        {created ? <><div>{created.date}</div><div className="text-[var(--tg-faint)]">{created.time}</div></> : "—"}
      </td>
      <td className="py-2.5 px-3 hidden lg:table-cell text-right tabular-nums text-xs text-[var(--c-amber)]">
        {j.captured ? j.captured.toLocaleString() : "—"}
      </td>
      <td className="py-2.5 px-3 text-right tabular-nums text-sm font-semibold">
        {(j.copied ?? 0).toLocaleString()}
        {j.total ? <span className="text-[var(--tg-faint)] font-normal text-xs"> / {j.total.toLocaleString()}</span> : null}
      </td>
      <td className="py-2.5 px-3 hidden xl:table-cell text-right font-mono text-[11px] text-[var(--tg-faint)]">
        {ckpt ? `#${ckpt}` : "—"}
      </td>
      <td className="py-2.5 px-3 hidden xl:table-cell text-right text-[11px] text-[var(--tg-faint)] whitespace-nowrap">
        {lastActivity || "—"}
      </td>
      <td className="py-2.5 px-3">
        <div className="flex items-center justify-end gap-1">
          {j.status === "running" && (
            <button onClick={() => onPause(j.id)} title="Pause" className="p-1.5 rounded hover:bg-[rgb(var(--c-violet-rgb)/0.10)] text-[var(--c-violet)]" data-testid={`pause-job-${j.id}`}><Pause className="h-3.5 w-3.5" /></button>
          )}
          {j.status === "paused" && (
            <button onClick={() => onResume(j.id)} title="Resume" className="p-1.5 rounded hover:bg-[rgb(var(--c-violet-rgb)/0.10)] text-[var(--c-violet)]" data-testid={`resume-job-${j.id}`}><Play className="h-3.5 w-3.5" /></button>
          )}
          {(j.status === "running" || j.status === "paused" || j.status === "queued" || j.status === "rate_limited") ? (
            <button onClick={() => onStop(j.id)} title="Stop" className="p-1.5 rounded hover:bg-[rgb(var(--c-amber-rgb)/0.10)] text-[var(--c-amber)]" data-testid={`stop-job-${j.id}`}><Square className="h-3.5 w-3.5" /></button>
          ) : (
            <button onClick={() => onStart(j.id)} title="Start" className="p-1.5 rounded hover:bg-[rgb(var(--c-emerald-rgb)/0.10)] text-[var(--c-emerald)]" data-testid={`start-job-${j.id}`}><Play className="h-3.5 w-3.5" /></button>
          )}
          <button onClick={() => onValidate(j.id)} disabled={validating} title="Validate job (checks access only — copies nothing)" className="p-1.5 rounded hover:bg-[var(--tg-hover)] text-[var(--tg-muted)] disabled:opacity-50" data-testid={`validate-job-${j.id}`}><ShieldCheck className={`h-3.5 w-3.5 ${validating ? "animate-pulse" : ""}`} /></button>
          {destBlocked && j.status !== "running" && (
            <button onClick={() => onRepickDestination(j)} title={diag.destination_blocked_reason || diag.last_validation?.reason} className="p-1.5 rounded hover:bg-[rgb(var(--c-rose-rgb)/0.10)] text-[var(--c-rose)]" data-testid={`repick-destination-${j.id}`}><Crosshair className="h-3.5 w-3.5" /></button>
          )}
          <button onClick={() => onLogs(j.id)} title="Logs" className="p-1.5 rounded hover:bg-[var(--tg-hover)] text-[var(--tg-muted)]" data-testid={`logs-job-${j.id}`}><ScrollText className="h-3.5 w-3.5" /></button>
          {jobActions(j).edit && (
            <button onClick={() => onEdit(j)} title="Edit" className="p-1.5 rounded hover:bg-[var(--tg-hover)] text-[var(--tg-muted)]" data-testid={`edit-job-${j.id}`}><Pencil className="h-3.5 w-3.5" /></button>
          )}
          <button onClick={() => onRemove(j.id)} title="Delete" className="p-1.5 rounded hover:bg-[rgb(var(--c-rose-rgb)/0.10)] text-[var(--tg-faint)] hover:text-[var(--c-rose)]" data-testid={`delete-job-${j.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </td>
    </tr>
  );
}

export default function Jobs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [channels, setChannels] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [rules, setRules] = useState([]);
  const [vaultChannels, setVaultChannels] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [massOpen, setMassOpen] = useState(false);
  const [editingMass, setEditingMass] = useState(null);
  const [logsFor, setLogsFor] = useState(null);
  const [validatingId, setValidatingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [view, setView] = useState(() => localStorage.getItem("clonecast_jobs_view") || "table");
  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || "all");
  const [modeFilter, setModeFilter] = useState(() => searchParams.get("mode") || "all");
  const [accountFilter, setAccountFilter] = useState(() => searchParams.get("account") || "all");
  const [sortBy, setSortBy] = useState(() => sortFromParams(searchParams));
  const [quick, setQuick] = useState(() => ["all", "running", "retry", "failed", "completed"].includes(searchParams.get("quick")) ? searchParams.get("quick") : "all");
  const [createdPreset, setCreatedPreset] = useState(() => {
    if (CREATED_VALUES.has(searchParams.get("created"))) return searchParams.get("created");
    return searchParams.get("created_from") || searchParams.get("created_to") ? "custom" : "any";
  });
  const [createdFrom, setCreatedFrom] = useState(() => searchParams.get("created_from") || "");
  const [createdTo, setCreatedTo] = useState(() => searchParams.get("created_to") || "");
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const initialFrom = localInputParts(searchParams.get("created_from"));
  const initialTo = localInputParts(searchParams.get("created_to"));
  const [draftFromDate, setDraftFromDate] = useState(initialFrom.date);
  const [draftFromTime, setDraftFromTime] = useState(initialFrom.time);
  const [draftToDate, setDraftToDate] = useState(initialTo.date);
  const [draftToTime, setDraftToTime] = useState(initialTo.time);
  const [page, setPage] = useState(() => validPage(searchParams.get("page"), 0));
  const [PAGE_SIZE, setPageSize] = useState(() => validPage(searchParams.get("page_size"), 50) || 50);
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  const load = useCallback(async () => {
    try {
      const [j, c, a, t] = await Promise.all([
        api.get("/jobs"), api.get("/channels"), api.get("/accounts"),
        api.get("/rule-templates"),
      ]);
      setJobs(j.data); setChannels(c.data); setAccounts(a.data); setTemplates(t.data);
    } catch (error) {
      console.error("jobs load failed", error);
    }
    // Needed only by the Replay form. Fetched separately so a Content Vault problem
    // can never take the whole Jobs page down with it.
    const [r, v] = await Promise.allSettled([api.get("/rules"), api.get("/vault/channels")]);
    if (r.status === "fulfilled") setRules(r.value.data);
    if (v.status === "fulfilled") setVaultChannels(v.value.data);
  }, []);

  useEffect(() => {
    load();
    const refreshProfiles = () => api.get("/rule-templates").then((r) => setTemplates(r.data)).catch(() => {});
    const onStorage = (e) => { if (e.key === "clonecast-scanner-profile") refreshProfiles(); };
    window.addEventListener("focus", refreshProfiles);
    window.addEventListener("storage", onStorage);
    const timer = setInterval(async () => {
      try { setJobs((await api.get("/jobs")).data); } catch (error) { console.error("jobs poll failed", error); }
    }, 5000);
    return () => { clearInterval(timer); window.removeEventListener("focus", refreshProfiles); window.removeEventListener("storage", onStorage); };
  }, [load]);

  const setViewPersist = (v) => { setView(v); localStorage.setItem("clonecast_jobs_view", v); };
  const createdRange = useMemo(() => createdBounds(createdPreset, new Date(), {
    from: createdFrom ? new Date(createdFrom) : null,
    to: createdTo ? new Date(createdTo) : null,
  }), [createdPreset, createdFrom, createdTo]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (search) next.set("q", search);
    if (statusFilter !== "all") next.set("status", statusFilter);
    if (modeFilter !== "all") next.set("mode", modeFilter);
    if (accountFilter !== "all") next.set("account", accountFilter);
    if (quick !== "all") next.set("quick", quick);
    if (createdPreset !== "any") next.set("created", createdPreset);
    if (createdPreset === "custom" && createdFrom) next.set("created_from", createdFrom);
    if (createdPreset === "custom" && createdTo) next.set("created_to", createdTo);
    if (sortBy === "created_asc") { next.set("sort", "created_at"); next.set("order", "asc"); }
    else if (sortBy === "activity_desc" || sortBy === "activity_asc") {
      next.set("sort", "last_activity"); next.set("order", sortBy.endsWith("asc") ? "asc" : "desc");
    } else if (sortBy === "name_asc" || sortBy === "name_desc") {
      next.set("sort", "name"); next.set("order", sortBy.endsWith("asc") ? "asc" : "desc");
    } else if (sortBy === "status") next.set("sort", "status");
    if (page) next.set("page", String(page));
    if (PAGE_SIZE !== 50) next.set("page_size", String(PAGE_SIZE));
    setSearchParams(next, { replace: true });
  }, [search, statusFilter, modeFilter, accountFilter, quick, createdPreset, createdFrom, createdTo, sortBy, page, PAGE_SIZE, setSearchParams]);

  const chooseCreatedPreset = (value) => {
    if (value === "custom") {
      const from = localInputParts(createdFrom);
      const to = localInputParts(createdTo);
      setDraftFromDate(from.date); setDraftFromTime(from.time);
      setDraftToDate(to.date); setDraftToTime(to.time);
      setCustomDateOpen(true);
      return;
    }
    setCreatedPreset(value);
    setCreatedFrom(""); setCreatedTo("");
  };

  const applyCustomDate = () => {
    const from = localInputToIso(draftFromDate, draftFromTime, false);
    const to = localInputToIso(draftToDate, draftToTime, true);
    if (!from && !to) return toast.error("Choose a From date, a To date, or both");
    if (from && to && new Date(from) > new Date(to)) return toast.error("From must be before To");
    setCreatedFrom(from); setCreatedTo(to); setCreatedPreset("custom"); setCustomDateOpen(false);
  };

  const clearCreated = () => {
    setCreatedPreset("any"); setCreatedFrom(""); setCreatedTo("");
    setDraftFromDate(""); setDraftFromTime(""); setDraftToDate(""); setDraftToTime("");
  };

  const sources = useMemo(() => channels.filter((channel) => channel.role === "source"), [channels]);
  const dests = useMemo(() => channels.filter((channel) => channel.role === "destination"), [channels]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const inQuick = (j) => {
      if (quick === "all") return true;
      if (quick === "running") return j.status === "running";
      if (quick === "retry") return j.live_retry_from != null || j.status === "rate_limited";
      if (quick === "failed") return ["error", "failed"].includes(j.status);
      if (quick === "completed") return j.status === "completed";
      return true;
    };
    let list = jobs.filter((j) => {
      if (!inQuick(j)) return false;
      if (statusFilter !== "all" && j.status !== statusFilter) return false;
      if (modeFilter !== "all" && j.mode !== modeFilter) return false;
      if (accountFilter !== "all") {
        const src = j.source_account_id || j.account_id;
        const dst = j.dest_account_id || j.account_id;
        if (src !== accountFilter && dst !== accountFilter) return false;
      }
      if (createdPreset !== "any" && !matchesCreatedRange(j.created_at, createdRange)) return false;
      if (!q) return true;
      return [j.name, j.source, ...(j.destinations || []), j.source_account_name, j.dest_account_name]
        .filter(Boolean).some((s) => String(s).toLowerCase().includes(q));
    });
    const bySort = {
      status: (a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) || String(a.name).localeCompare(String(b.name)),
      name_asc: (a, b) => String(a.name).localeCompare(String(b.name)),
      name_desc: (a, b) => String(b.name).localeCompare(String(a.name)),
      created_desc: compareDate("created_at", "desc"),
      created_asc: compareDate("created_at", "asc"),
      activity_desc: compareDate("last_copied_at", "desc"),
      activity_asc: compareDate("last_copied_at", "asc"),
    };
    return [...list].sort(bySort[sortBy] || bySort.created_desc);
  }, [jobs, search, statusFilter, modeFilter, accountFilter, createdPreset, createdRange, sortBy, quick]);

  useEffect(() => {
    if (!filtersInitialized) { setFiltersInitialized(true); return; }
    setPage(0);
  }, [search, statusFilter, modeFilter, accountFilter, createdPreset, createdFrom, createdTo, sortBy, quick, PAGE_SIZE]); // eslint-disable-line react-hooks/exhaustive-deps
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageJobs = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  useEffect(() => {
    if (page >= pageCount) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  const counts = useMemo(() => {
    const c = { running: 0, error: 0, paused: 0, queued: 0, completed: 0, retry: 0 };
    jobs.forEach((j) => {
      if (c[j.status] !== undefined) c[j.status] += 1;
      if (j.live_retry_from != null || j.status === "rate_limited") c.retry += 1;
    });
    return c;
  }, [jobs]);

  const QUICK = [
    { key: "all", label: `All ${jobs.length}`, tone: "var(--tg-faint)" },
    { key: "running", label: `Running ${counts.running}`, tone: "var(--c-emerald)" },
    { key: "retry", label: `Retry ${counts.retry}`, tone: "var(--c-pink)" },
    { key: "failed", label: `Failed ${counts.error}`, tone: "var(--c-rose)" },
    { key: "completed", label: `Completed ${counts.completed}`, tone: "var(--c-sky)" },
  ];

  const toggle = (field, id) => setForm((current) => ({
    ...current,
    [field]: current[field].includes(id) ? current[field].filter((value) => value !== id) : [...current[field], id],
  }));
  // Every job type goes through the same two steps: validate the form the way the
  // backend will, then send exactly the request its model accepts (jobForm.js).
  const save = async (startAfter = false) => {
    const problem = validateForm(form, { editing: Boolean(editingId) });
    if (problem) return toast.error(problem);
    try {
      const req = buildRequest(form, editingId);
      const { data } = await api[req.method](req.path, req.body);
      if (startAfter && data?.id) await api.post(`/jobs/${data.id}/start`);
      toast.success(editingId ? "Job updated" : startAfter ? "Job created and started" : "Job created");
      setOpen(false); setEditingId(null); setForm(emptyForm); load();
    } catch (error) { toast.error(errMsg(error, "Failed to save job")); }
  };
  const edit = (job) => {
    if (job.mode === "mass_replace") {
      setEditingMass(job);
      setMassOpen(true);
      return;
    }
    setEditingId(job.id);
    setForm(formFromJob(job));
    setOpen(true);
  };
  const action = async (id, name) => {
    try { await api.post(`/jobs/${id}/${name}`); load(); } catch (error) { toast.error(errMsg(error, `Failed to ${name}`)); }
  };
  // Read-only pre-flight. The backend copies nothing during validation, so this is
  // safe to run on a live job as well as before starting a stopped one.
  const validate = async (id) => {
    setValidatingId(id);
    try {
      const { data } = await api.post(`/jobs/${id}/validate`);
      if (data.state === "READY") toast.success("READY — this job can run.");
      else toast.error(`VALIDATION FAILED — ${data.reason}`, { duration: 10000 });
      load();
    } catch (error) {
      toast.error(errMsg(error, "Could not validate this job"));
    } finally {
      setValidatingId(null);
    }
  };
  // Re-picking a destination is an edit, not a guess: open the job editor rather
  // than choosing a replacement channel on the operator's behalf.
  const repickDestination = (job) => {
    toast.info("Choose a destination this account can post to, then save.");
    edit(job);
  };
  const remove = async (id) => {
    const job = jobs.find((j) => j.id === id);
    if (!window.confirm(`Delete job "${job?.name || id}"? Its archive in the Content Vault is kept.`)) return;
    try { await api.delete(`/jobs/${id}`); load(); } catch (error) { toast.error(errMsg(error, "Failed to delete job")); }
  };
  const logs = jobs.find((job) => job.id === logsFor)?.logs || [];
  const handlers = { onStart: (id) => action(id, "start"), onStop: (id) => action(id, "stop"),
    onPause: (id) => action(id, "pause"), onResume: (id) => action(id, "resume"),
    onLogs: setLogsFor, onEdit: edit, onRemove: remove,
    onValidate: validate, onRepickDestination: repickDestination };

  return <div className="w-full max-w-[1800px] space-y-5">
    <div className="flex flex-wrap justify-between items-center gap-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="label-caps">Copy Jobs ({jobs.length})</div>
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
          {counts.running > 0 && <span className="px-2 py-0.5 rounded-full" style={{ color: "var(--c-emerald)", background: "rgb(var(--c-emerald-rgb)/0.1)" }}>{counts.running} running</span>}
          {counts.queued > 0 && <span className="px-2 py-0.5 rounded-full" style={{ color: "var(--tg-faint)", background: "rgb(var(--tg-faint-rgb)/0.1)" }}>{counts.queued} queued</span>}
          {counts.paused > 0 && <span className="px-2 py-0.5 rounded-full" style={{ color: "var(--c-violet)", background: "rgb(var(--c-violet-rgb)/0.1)" }}>{counts.paused} paused</span>}
          {counts.error > 0 && <span className="px-2 py-0.5 rounded-full" style={{ color: "var(--c-rose)", background: "rgb(var(--c-rose-rgb)/0.1)" }}>{counts.error} failed</span>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <MassReplaceDialog
          open={massOpen}
          onOpenChange={(value) => { setMassOpen(value); if (!value) setEditingMass(null); }}
          accounts={accounts}
          destChannels={dests}
          templates={templates}
          editingJob={editingMass}
          onCreated={load}
        />
        <JobFormDialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) { setEditingId(null); setForm(emptyForm); } }} onNewClick={() => { setEditingId(null); setForm(emptyForm); setOpen(true); }} editingId={editingId} form={form} setForm={setForm} accounts={accounts} sources={sources} dests={dests} templates={templates} rules={rules} vaultChannels={vaultChannels} onSubmit={() => save(false)} onSubmitAndStart={() => save(true)} toggleDest={(id) => toggle("destinations", id)} toggle={toggle} onPickMassReplace={() => { setOpen(false); setEditingId(null); setForm(emptyForm); setEditingMass(null); setMassOpen(true); }} />
      </div>
    </div>

    <OperationHistory refresh={massOpen} />
    {/* quick filters — one click to the states that matter operationally */}
    <div className="flex flex-wrap items-center gap-1.5" data-testid="jobs-quick-filters">
      {QUICK.map((q) => {
        const active = quick === q.key;
        return (
          <button
            key={q.key}
            type="button"
            onClick={() => setQuick(q.key)}
            data-testid={`jobs-quick-${q.key}`}
            className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full transition-colors border"
            style={{
              color: q.tone,
              background: active ? `color-mix(in srgb, ${q.tone} 18%, transparent)` : "transparent",
              borderColor: active ? q.tone : "var(--tg-border)",
            }}
          >
            {q.label}
          </button>
        );
      })}
    </div>

    <div className="flex flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tg-faint)]" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search jobs, channels, accounts…"
               data-testid="jobs-search" className="bg-[var(--tg-bg)] border-[var(--tg-border)] pl-9 h-9 focus:border-[var(--c-sky)]" />
      </div>
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-32 h-9 bg-[var(--tg-bg)] border-[var(--tg-border)]" data-testid="jobs-status-filter"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="running">Running</SelectItem>
          <SelectItem value="queued">Queued</SelectItem>
          <SelectItem value="rate_limited">Rate limited</SelectItem>
          <SelectItem value="paused">Paused</SelectItem>
          <SelectItem value="stopped">Stopped</SelectItem>
          <SelectItem value="completed">Completed</SelectItem>
          <SelectItem value="error">Failed</SelectItem>
          <SelectItem value="idle">Idle</SelectItem>
        </SelectContent>
      </Select>
      <Select value={modeFilter} onValueChange={setModeFilter}>
        <SelectTrigger className="w-36 h-9 bg-[var(--tg-bg)] border-[var(--tg-border)]" data-testid="jobs-mode-filter"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
          <SelectItem value="all">All modes</SelectItem>
          <SelectItem value="live">Live</SelectItem>
          <SelectItem value="historical">Historical</SelectItem>
          <SelectItem value="mass_replace">Mass replace</SelectItem>
          <SelectItem value="archive">Archive</SelectItem>
          <SelectItem value="replay">Replay</SelectItem>
        </SelectContent>
      </Select>
      <Select value={accountFilter} onValueChange={setAccountFilter}>
        <SelectTrigger className="w-36 h-9 bg-[var(--tg-bg)] border-[var(--tg-border)]" data-testid="jobs-account-filter"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
          <SelectItem value="all">All accounts</SelectItem>
          {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={createdPreset} onValueChange={chooseCreatedPreset}>
        <SelectTrigger className={`w-40 h-9 bg-[var(--tg-bg)] ${createdPreset !== "any" ? "border-[var(--c-sky)] text-[var(--c-sky)]" : "border-[var(--tg-border)]"}`} data-testid="jobs-created-filter">
          <CalendarDays className="h-3.5 w-3.5 mr-1.5" /><SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
          {Object.entries(CREATED_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={sortBy} onValueChange={setSortBy}>
        <SelectTrigger className="w-52 h-9 bg-[var(--tg-bg)] border-[var(--tg-border)]" data-testid="jobs-sort"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
          <SelectItem value="status">Sort: status</SelectItem>
          <SelectItem value="created_desc">Created — newest first</SelectItem>
          <SelectItem value="created_asc">Created — oldest first</SelectItem>
          <SelectItem value="activity_desc">Last activity — newest first</SelectItem>
          <SelectItem value="activity_asc">Last activity — oldest first</SelectItem>
          <SelectItem value="name_asc">Job name A–Z</SelectItem>
          <SelectItem value="name_desc">Job name Z–A</SelectItem>
        </SelectContent>
      </Select>
      <div className="flex rounded-md border border-[var(--tg-border)] overflow-hidden">
        <button onClick={() => setViewPersist("cards")} title="Card view" data-testid="jobs-view-cards"
                className={`p-2 ${view === "cards" ? "bg-[rgb(var(--c-sky-rgb)/0.15)] text-[var(--c-sky)]" : "text-[var(--tg-faint)] hover:bg-[var(--tg-hover)]"}`}>
          <LayoutGrid className="h-4 w-4" />
        </button>
        <button onClick={() => setViewPersist("table")} title="Table view" data-testid="jobs-view-table"
                className={`p-2 ${view === "table" ? "bg-[rgb(var(--c-sky-rgb)/0.15)] text-[var(--c-sky)]" : "text-[var(--tg-faint)] hover:bg-[var(--tg-hover)]"}`}>
          <List className="h-4 w-4" />
        </button>
      </div>
    </div>

    {createdPreset !== "any" && (
      <div className="flex items-center gap-2" data-testid="jobs-created-active-filter">
        <span className="inline-flex items-center gap-2 rounded-full border border-[rgb(var(--c-sky-rgb)/0.30)] bg-[rgb(var(--c-sky-rgb)/0.08)] px-3 py-1 text-xs text-[var(--c-sky)]">
          Created: {createdFilterLabel(createdPreset, createdRange)}
          <button type="button" onClick={clearCreated} title="Clear Created filter" data-testid="clear-created-filter"><X className="h-3.5 w-3.5" /></button>
        </span>
      </div>
    )}

    {filtered.length === 0 ? (
      <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-12 text-center" data-testid="no-jobs">
        <ScrollText className="h-8 w-8 mx-auto text-[var(--tg-faint)]" />
        <p className="text-[var(--tg-faint)] mt-3 text-sm">
          {jobs.length === 0 ? "No jobs yet. Create one to start copying."
            : createdPreset !== "any" ? "No copy jobs found for this date range."
            : "No jobs match the current filters."}
        </p>
        {createdPreset !== "any" && <Button size="sm" variant="outline" onClick={clearCreated} className="mt-4 border-[var(--tg-border)]" data-testid="empty-clear-created">Clear Created filter</Button>}
      </Card>
    ) : view === "table" ? (
      <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-360px)]">
        <table className="w-full text-left min-w-[1040px]">
          <thead className="sticky top-0 z-10 bg-[var(--tg-surface)]">
            <tr className="border-b border-[var(--tg-border)]">
              <th className="py-2.5 px-3 label-caps font-medium">Job · Route</th>
              <th className="py-2.5 px-3 label-caps font-medium hidden lg:table-cell">Mode</th>
              <th className="py-2.5 px-3 label-caps font-medium hidden md:table-cell">Account</th>
              <th className="py-2.5 px-3 label-caps font-medium">Status</th>
              <th className="py-2.5 px-3 label-caps font-medium hidden lg:table-cell">
                <button type="button" onClick={() => setSortBy(sortBy === "created_desc" ? "created_asc" : "created_desc")}
                  className={`inline-flex items-center gap-1 hover:text-[var(--tg-strong)] ${sortBy.startsWith("created_") ? "text-[var(--c-sky)]" : ""}`}
                  title="Sort by Created" data-testid="sort-created-column">
                  Created
                  {sortBy === "created_desc" ? <ArrowDown className="h-3 w-3" />
                    : sortBy === "created_asc" ? <ArrowUp className="h-3 w-3" />
                    : <ChevronsUpDown className="h-3 w-3" />}
                </button>
              </th>
              <th className="py-2.5 px-3 label-caps font-medium text-right hidden lg:table-cell">Captured</th>
              <th className="py-2.5 px-3 label-caps font-medium text-right">Delivered</th>
              <th className="py-2.5 px-3 label-caps font-medium text-right hidden xl:table-cell">Checkpoint</th>
              <th className="py-2.5 px-3 label-caps font-medium text-right hidden xl:table-cell">Last Activity</th>
              <th className="py-2.5 px-3 label-caps font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageJobs.map((j) => <JobTableRow key={j.id} j={j} validating={validatingId === j.id} {...handlers} />)}
          </tbody>
        </table>
        </div>
      </Card>
    ) : (
      <div className="space-y-4">
        {pageJobs.map((job) => <JobCard key={job.id} job={job} accounts={accounts} templates={templates} validating={validatingId === job.id} {...handlers} />)}
      </div>
    )}

    {filtered.length > PAGE_SIZE && (
      <Pagination
        page={page} pageCount={pageCount} total={filtered.length}
        from={page * PAGE_SIZE + 1} to={Math.min((page + 1) * PAGE_SIZE, filtered.length)}
        pageSize={PAGE_SIZE} onPageSize={setPageSize}
        onPrev={() => setPage(Math.max(0, page - 1))}
        onNext={() => setPage(Math.min(pageCount - 1, page + 1))}
        testid="jobs-pagination"
      />
    )}

    <Dialog open={customDateOpen} onOpenChange={setCustomDateOpen}>
      <DialogContent className="bg-[var(--tg-surface)] border-[var(--tg-border)] text-[var(--tg-strong)] max-w-md">
        <DialogHeader><DialogTitle className="font-head">Custom Created Range</DialogTitle></DialogHeader>
        <div className="space-y-4" data-testid="created-custom-range">
          <div className="space-y-2">
            <div className="label-caps">From</div>
            <div className="grid grid-cols-[1fr_130px] gap-2">
              <Input type="date" value={draftFromDate} onChange={(event) => setDraftFromDate(event.target.value)} className="bg-[var(--tg-bg)] border-[var(--tg-border)]" data-testid="created-from-date" />
              <Input type="time" value={draftFromTime} onChange={(event) => setDraftFromTime(event.target.value)} className="bg-[var(--tg-bg)] border-[var(--tg-border)]" data-testid="created-from-time" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="label-caps">To</div>
            <div className="grid grid-cols-[1fr_130px] gap-2">
              <Input type="date" value={draftToDate} onChange={(event) => setDraftToDate(event.target.value)} className="bg-[var(--tg-bg)] border-[var(--tg-border)]" data-testid="created-to-date" />
              <Input type="time" value={draftToTime} onChange={(event) => setDraftToTime(event.target.value)} className="bg-[var(--tg-bg)] border-[var(--tg-border)]" data-testid="created-to-time" />
            </div>
          </div>
          <p className="text-[11px] text-[var(--tg-faint)]">Leave time empty to use the start of the From date and end of the To date.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCustomDateOpen(false)} className="border-[var(--tg-border)]">Cancel</Button>
            <Button onClick={applyCustomDate} className="bg-[var(--c-sky)] text-white" data-testid="apply-created-range">Apply</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={!!logsFor} onOpenChange={(value) => !value && setLogsFor(null)}><DialogContent className="bg-[var(--tg-surface)] border-[var(--tg-border)] text-[var(--tg-strong)] max-w-2xl"><DialogHeader><DialogTitle className="font-head">Job Logs</DialogTitle></DialogHeader><div className="bg-[var(--tg-bg)] border border-[var(--tg-border)] rounded-md p-4 max-h-96 overflow-y-auto font-mono text-xs space-y-1" data-testid="job-logs">{logs.length === 0 && <p className="text-[var(--tg-faint)]">No log entries yet.</p>}{logs.slice().reverse().map((entry, index) => <div key={`${entry.ts}-${index}`} className="text-[var(--tg-muted)]"><span className="text-[var(--tg-faint)]">{new Date(entry.ts).toLocaleTimeString()} </span>{entry.text}</div>)}</div></DialogContent></Dialog>
  </div>;
}
